'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import MarketingLayout from '@/components/MarketingLayout';

const stats = [
  { value: '90%', label: 'Faster than traditional bolting', sub: 'Up to 100× in some applications' },
  { value: '<5%', label: 'Preload variation', sub: 'Uniform flange load every time' },
  { value: '~105%', label: 'First-event ROI', sub: 'Payback in under one shutdown cycle' },
  { value: '26%', label: 'Of flange leaks from improper install', sub: 'BridgeFlow eliminates the root cause' },
];

const processSteps = [
  { num: '01', title: 'Position the System', desc: 'Place BridgeFlow™ MTE around the flange. No flange modification required — fully compatible with ASME B16.5.' },
  { num: '02', title: 'Encircle & Align', desc: 'The system encircles the pipe and driving heads automatically align to each bolt position.' },
  { num: '03', title: 'One Stroke Operation', desc: 'A single input simultaneously engages all bolts. Resistance-based equilibrium automatically routes torque to the loosest bolt first.' },
  { num: '04', title: 'Verified Integrity', desc: 'Dynamic torque redistribution achieves equilibrium across all bolts. Digital version records final torque per head for QC documentation.' },
];

const differentiators = [
  {
    title: 'Simultaneous Multi-Bolt Tightening',
    desc: 'All bolts tighten at once — not sequentially. Eliminates the star pattern entirely and the human error that comes with it.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="3"/><circle cx="4" cy="4" r="2"/><circle cx="18" cy="4" r="2"/><circle cx="4" cy="18" r="2"/><circle cx="18" cy="18" r="2"/>
        <path d="M6 6l3.5 3.5M12.5 12.5L16 16M16 6l-3.5 3.5M9.5 12.5L6 16"/>
      </svg>
    ),
  },
  {
    title: 'Automatic Torque Redistribution',
    desc: 'Mechanical resistance-based equilibrium — torque flows to the loosest bolt automatically until all bolts reach the same preload.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 11h14M11 4l7 7-7 7"/><path d="M4 7l3 4-3 4" opacity="0.5"/>
      </svg>
    ),
  },
  {
    title: 'Engineered Preload Equalization',
    desc: 'Achieves <5% preload variation — not operator-dependent. Uniform gasket compression prevents leaks at the source.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 11h16M3 7h16M3 15h16"/><circle cx="8" cy="11" r="1.5" fill="currentColor"/><circle cx="14" cy="7" r="1.5" fill="currentColor"/><circle cx="11" cy="15" r="1.5" fill="currentColor"/>
      </svg>
    ),
  },
  {
    title: 'Shutdown Acceleration',
    desc: '8 minutes per flange vs. 45 minutes traditionally. 40 flanges completed in 5.3 hours instead of 30 hours.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="M11 7v4l3 3"/>
      </svg>
    ),
  },
  {
    title: 'Reduced Operator Dependency',
    desc: 'The system embeds integrity into its mechanics — not into technician skill. Works consistently regardless of crew experience.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a4 4 0 014 4 4 4 0 01-4 4 4 4 0 01-4-4 4 4 0 014-4z"/><path d="M4 20v-1a8 8 0 0116 0v1"/>
        <path d="M16 12l2 2 4-4" strokeWidth="2"/>
      </svg>
    ),
  },
  {
    title: 'Digital Integrity Documentation',
    desc: 'Optional wireless real-time torque monitoring per driving head, final torque recording, and digital QC audit trail.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="16" height="16" rx="2"/><path d="M7 8h8M7 12h5M7 16h3"/>
      </svg>
    ),
  },
];

export default function HomePage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-[#0A0F1A]" />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(ellipse 80% 60% at 60% 40%, rgba(249,115,22,0.07) 0%, transparent 60%), radial-gradient(ellipse 50% 50% at 20% 80%, rgba(59,130,246,0.05) 0%, transparent 50%)',
        }} />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(#F97316 1px, transparent 1px), linear-gradient(90deg, #F97316 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-24 lg:pt-40 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left content */}
            <div className="lg:col-span-6 xl:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F97316]/10 border border-[#F97316]/20 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] animate-pulse" />
                <span className="text-[#F97316] text-xs font-semibold tracking-wider uppercase">Patented MTE Technology</span>
              </div>

              <h1 className="text-hero-xl text-[#F1F5F9] mb-6 leading-[1.08]">
                Flange Integrity
                <br />
                <span className="text-[#F97316]">Without Compromise.</span>
                <br />
                Shutdowns
                <br />
                <span className="relative">
                  Without Delay.
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-[#F97316] to-transparent" />
                </span>
              </h1>

              <p className="text-[#94A3B8] text-lg leading-relaxed mb-8 max-w-lg">
                BridgeFlow™ MTE simultaneously tightens all flange bolts in a single stroke eliminating sequential star-pattern bolting, operator variability, and flange leaks caused by improper installation.
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <a
                  href="mailto:info@irongatesupply.com?subject=Request%20a%20demo"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#F97316] text-white font-semibold hover:bg-[#EA580C] transition-all duration-150 shadow-xl shadow-orange-500/25 active:scale-95"
                >
                  Request a Demo
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
                <a
                  href="https://vvkhbqgeoyhuvzljcqes.supabase.co/storage/v1/object/sign/Info/BridgeFlow%20MTE%20System%2020260423.pdf?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV84ZTQ5NTUwYS0xMDhlLTQ5YTUtOGY5MC0yNjU5ZmY4YmQ4MDYiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJJbmZvL0JyaWRnZUZsb3cgTVRFIFN5c3RlbSAyMDI2MDQyMy5wZGYiLCJpYXQiOjE3Nzg0NDgxODUsImV4cCI6MTgwOTk4NDE4NX0.FPsP-CBq_ioS3kfEbQS0F9cEcOhH0lVD1TBtTQkkMa0"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#1E293B] text-[#F1F5F9] font-semibold hover:bg-[#263348] transition-all duration-150 border border-[#334155] active:scale-95"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 2v8M5 7l3 3 3-3M3 13h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Download Brochure
                </a>
                <Link
                  href="/roi-calculator"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#1E293B] text-[#F1F5F9] font-semibold hover:bg-[#263348] transition-all duration-150 border border-[#334155] active:scale-95"
                >
                  Calculate Your ROI
                </Link>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-3">
                {['ASME B16.5 Compatible', 'No Flange Modification', 'Patented Technology']?.map((badge) => (
                  <span key={badge} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1E293B] border border-[#334155] text-[#94A3B8] text-xs font-medium">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 5l2 2 4-4" stroke="#22C55E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {badge}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — visual */}
            <div className="lg:col-span-6 xl:col-span-7 flex justify-center lg:justify-end">
              <div className="relative w-full max-w-2xl">
                <div className="relative rounded-2xl overflow-hidden border border-[#F97316]/20 shadow-2xl shadow-orange-500/10" style={{ paddingBottom: '56.25%', height: 0 }}>
                  <iframe
                    src="https://www.youtube.com/embed/DfG-Z1u5I-M"
                    title="BridgeFlow™ MTE System"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute top-0 left-0 w-full h-full"
                    style={{ border: 'none' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Stats bar */}
      <section className="border-y border-[#1E293B] bg-[#060B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats?.map((stat) => (
              <div key={stat?.value} className="text-center">
                <div className="font-mono text-3xl lg:text-4xl font-bold text-[#F97316] mb-1">{stat?.value}</div>
                <div className="text-[#F1F5F9] text-sm font-semibold mb-0.5">{stat?.label}</div>
                <div className="text-[#64748B] text-xs">{stat?.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* How it works */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">How It Works</span>
            <h2 className="text-display text-[#F1F5F9] mt-3 mb-4">Four steps. One stroke. Zero compromise.</h2>
            <p className="text-[#64748B] text-lg leading-relaxed">
              BridgeFlow™ MTE replaces the entire multi-pass sequential bolting process with a single synchronized operation — engineered around flange behavior, not just bolt tightening.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {processSteps?.map((step, i) => (
              <div
                key={step?.num}
                className="relative rounded-2xl border border-[#1E293B] bg-[#111827] p-6 hover:border-[#F97316]/30 transition-all duration-300 group"
              >
                {i < processSteps?.length - 1 && (
                  <div className="hidden xl:block absolute top-10 -right-3 w-6 h-0.5 bg-gradient-to-r from-[#F97316]/40 to-transparent z-10" />
                )}
                <div className="font-mono text-[#F97316]/30 text-5xl font-bold mb-4 group-hover:text-[#F97316]/50 transition-colors">{step?.num}</div>
                <h3 className="text-[#F1F5F9] font-bold text-lg mb-3">{step?.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{step?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Differentiators — bento grid */}
      <section className="py-24 lg:py-32 bg-[#060B14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">Why BridgeFlow™</span>
            <h2 className="text-display text-[#F1F5F9] mt-3 mb-4">Traditional technologies perfected controlled bolting. BridgeFlow reimagines it.</h2>
            <p className="text-[#64748B] text-lg leading-relaxed">
              Traditional hydraulic systems improve torque delivery. BridgeFlow transforms load equalization — embedding flange integrity into the system itself.
            </p>
          </div>

          {/* Bento grid — varied sizes */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {/* Large card */}
            <div className="md:col-span-2 xl:col-span-2 rounded-2xl border border-[#1E293B] bg-gradient-to-br from-[#111827] to-[#0F172A] p-8 hover:border-[#F97316]/30 transition-all duration-300 group">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#F97316]/10 border border-[#F97316]/20 flex items-center justify-center text-[#F97316] flex-shrink-0 group-hover:bg-[#F97316]/15 transition-colors">
                  {differentiators?.[0]?.icon}
                </div>
                <div>
                  <h3 className="text-[#F1F5F9] font-bold text-xl mb-2">{differentiators?.[0]?.title}</h3>
                  <p className="text-[#64748B] leading-relaxed">{differentiators?.[0]?.desc}</p>
                </div>
              </div>
              {/* Visual comparison */}
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="rounded-xl bg-[#0A0F1A] border border-[#EF4444]/20 p-4">
                  <p className="text-[#EF4444] text-xs font-semibold uppercase tracking-wider mb-2">Traditional Method</p>
                  <p className="text-[#94A3B8] text-sm">3–5 sequential passes · Star pattern required · Operator-dependent · Re-torquing needed</p>
                </div>
                <div className="rounded-xl bg-[#0A0F1A] border border-[#22C55E]/20 p-4">
                  <p className="text-[#22C55E] text-xs font-semibold uppercase tracking-wider mb-2">BridgeFlow™ MTE</p>
                  <p className="text-[#94A3B8] text-sm">1 stroke · No star pattern · Automatic equilibrium · Verified preload</p>
                </div>
              </div>
            </div>

            {/* Regular cards */}
            {differentiators?.slice(1)?.map((d) => (
              <div
                key={d?.title}
                className="rounded-2xl border border-[#1E293B] bg-[#111827] p-6 hover:border-[#F97316]/30 transition-all duration-300 group"
              >
                <div className="w-11 h-11 rounded-xl bg-[#F97316]/10 border border-[#F97316]/20 flex items-center justify-center text-[#F97316] mb-4 group-hover:bg-[#F97316]/15 transition-colors">
                  {d?.icon}
                </div>
                <h3 className="text-[#F1F5F9] font-bold text-base mb-2">{d?.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{d?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ROI Teaser */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="rounded-2xl border border-[#F97316]/20 bg-gradient-to-br from-[#111827] via-[#0F172A] to-[#0A0F1A] p-8 lg:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#F97316]/5 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">ROI Framework</span>
                <h2 className="text-display text-[#F1F5F9] mt-3 mb-4">One hour of avoided downtime often pays for the entire system.</h2>
                <p className="text-[#64748B] text-lg leading-relaxed mb-6">
                  At $20,000/hour downtime cost, 40 flanges, and a 4-person crew — BridgeFlow™ MTE saves $511,000 per shutdown event against a $40,000 unit investment.
                </p>
                <Link
                  href="/roi-calculator"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#F97316] text-white font-semibold hover:bg-[#EA580C] transition-all duration-150 shadow-xl shadow-orange-500/25 active:scale-95"
                >
                  Calculate Your Savings
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Hours Saved', value: '24.7h', sub: 'Per shutdown event', color: 'text-[#22C55E]' },
                  { label: 'Downtime Recovery', value: '$494K', sub: 'Production value recovered', color: 'text-[#F97316]' },
                  { label: 'Labor Savings', value: '$17.3K', sub: 'Per event', color: 'text-[#3B82F6]' },
                  { label: 'First-Event ROI', value: '~105%', sub: 'Payback &lt; 1 shutdown', color: 'text-[#F97316]' },
                ]?.map((item) => (
                  <div key={item?.label} className="rounded-xl bg-[#0A0F1A] border border-[#1E293B] p-5">
                    <p className="text-[#64748B] text-xs mb-1">{item?.label}</p>
                    <p className={`font-mono font-bold text-2xl ${item?.color}`}>{item?.value}</p>
                    <p className="text-[#475569] text-xs mt-1" dangerouslySetInnerHTML={{ __html: item?.sub }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Industries strip */}
      <section className="py-16 bg-[#060B14] border-y border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-shrink-0 lg:w-64">
              <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">Target Industries</span>
              <h3 className="text-[#F1F5F9] font-bold text-xl mt-2">Where downtime costs $10K–$100K+/hour</h3>
            </div>
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
              {['Pipeline & Gas Transmission', 'LNG Terminals', 'Refining & Petrochemical', 'Offshore Platforms', 'Hydrogen Infrastructure', 'Power Generation']?.map((ind) => (
                <Link
                  key={ind}
                  href="/industries"
                  className="px-4 py-2 rounded-lg bg-[#111827] border border-[#1E293B] text-[#94A3B8] text-sm font-medium hover:border-[#F97316]/40 hover:text-[#F97316] transition-all duration-150"
                >
                  {ind}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Final CTA */}
      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">Get Started</span>
          <h2 className="text-display text-[#F1F5F9] mt-4 mb-6">
            Ready to eliminate sequential bolting from your shutdown process?
          </h2>
          <p className="text-[#64748B] text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Talk to our team about your specific flange scope, downtime costs, and how BridgeFlow™ MTE fits your next planned outage.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="mailto:info@irongatesupply.com?subject=Request%20a%20demo"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#F97316] text-white font-semibold text-lg hover:bg-[#EA580C] transition-all duration-150 shadow-xl shadow-orange-500/25 active:scale-95"
            >
              Request a Demo
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M3 9h12M10 5l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
            <a
              href="https://vvkhbqgeoyhuvzljcqes.supabase.co/storage/v1/object/sign/Info/BridgeFlow%20MTE%20System%2020260423.pdf?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV84ZTQ5NTUwYS0xMDhlLTQ5YTUtOGY5MC0yNjU5ZmY4YmQ4MDYiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJJbmZvL0JyaWRnZUZsb3cgTVRFIFN5c3RlbSAyMDI2MDQyMy5wZGYiLCJpYXQiOjE3Nzg0NDgxODUsImV4cCI6MTgwOTk4NDE4NX0.FPsP-CBq_ioS3kfEbQS0F9cEcOhH0lVD1TBtTQkkMa0"
              download
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#1E293B] text-[#F1F5F9] font-semibold text-lg hover:bg-[#263348] transition-all duration-150 border border-[#334155] active:scale-95"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M9 2v10M5 8l4 4 4-4M3 15h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Download Brochure
            </a>
            <Link
              href="/product"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#1E293B] text-[#F1F5F9] font-semibold text-lg hover:bg-[#263348] transition-all duration-150 border border-[#334155] active:scale-95"
            >
              Explore the Product
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}